import { useReducer } from 'react'

import { Chip } from '@joor/design-system'

import { Attachment, OrderDiscountType } from '__generated__/atlas-types'

export type PaymentAmount = {
  amount: string
  format: OrderDiscountType
}

type EmailFields = {
  emails: Chip[]
  subject: string
  body: string
  attachment?: Attachment
  attachmentLoading: boolean
  paymentAmount: PaymentAmount
  paymentName: string
  dueDate?: string
}

type ACTIONS =
  | 'UPDATE_EMAILS'
  | 'UPDATE_EMAIL_SUBJECT'
  | 'UPDATE_EMAIL_BODY'
  | 'UPDATE_ATTACHMENT'
  | 'UPDATE_ATTACHMENT_LOADING'
  | 'UPDATE_PARTIAL_PAYMENT_AMOUNT'
  | 'UPDATE_PARTIAL_PAYMENT_NAME'
  | 'RESET_ATTACHMENT'
  | 'RESET_PAYMENT'
  | 'UPDATE_DUE_DATE'

/**
 * used a reducer to save us having a total of 6 or 7 useStates that would have to
 * be passed down all around. yeh claudia i know you hate it and we're early-optimizing here
 * but i think this will help us when we need to add the partial amount, payment name, and due date here.
 * it will let us add and test logic when saving values in the state if necessary and will make it easier for us
 * if we want to add a context later to not pass props down 4 or 5 levels :]
 */
const emailFieldsReducer = (
  state: EmailFields,
  action: { type: ACTIONS; payload?: any },
) => {
  // TODO:
  // unless there's any validation or logic you want to add, any new field should just
  // need to follow the other ones, just creating a new action. that it.
  switch (action.type) {
    case 'UPDATE_EMAILS':
      return {
        ...state,
        emails: action.payload as Chip[],
      }
    case 'UPDATE_EMAIL_SUBJECT':
      return {
        ...state,
        subject: action.payload as string,
      }

    case 'UPDATE_EMAIL_BODY':
      return {
        ...state,
        body: action.payload as string,
      }
    case 'UPDATE_ATTACHMENT':
      return {
        ...state,
        attachment: action.payload as Attachment,
      }
    case 'UPDATE_ATTACHMENT_LOADING':
      return {
        ...state,
        attachmentLoading: action.payload as boolean,
      }
    case 'UPDATE_PARTIAL_PAYMENT_AMOUNT':
      return {
        ...state,
        paymentAmount: action.payload as {
          amount: string
          format: OrderDiscountType
        },
      }
    case 'UPDATE_PARTIAL_PAYMENT_NAME':
      return {
        ...state,
        paymentName: action.payload as string,
      }
    case 'UPDATE_DUE_DATE':
      return {
        ...state,
        dueDate: action.payload as string,
      }
    case 'RESET_ATTACHMENT':
      return {
        ...state,
        attachment: undefined,
      }
    case 'RESET_PAYMENT':
      return {
        ...state,
        paymentName: '',
        paymentAmount: {
          amount: '0',
          format: OrderDiscountType.NUMERIC,
        },
      }
    default:
      return state
  }
}

export const useEmailFields = () => {
  const [emailFields, dispatch] = useReducer(emailFieldsReducer, {
    emails: [],
    subject: '',
    body: '',
    attachment: undefined,
    attachmentLoading: false,
    paymentAmount: {
      amount: '0',
      format: OrderDiscountType.NUMERIC,
    },
    paymentName: '',
    dueDate: '',
  })

  const handleUploading = (attachment: Attachment) => {
    dispatch({
      type: 'UPDATE_ATTACHMENT',
      payload: attachment,
    })
  }

  const handleUpdateAttachmentLoading = (attachmentLoading: boolean) => {
    dispatch({
      type: 'UPDATE_ATTACHMENT_LOADING',
      payload: attachmentLoading,
    })
  }

  const handleDeleteAttachment = () => {
    dispatch({
      type: 'RESET_ATTACHMENT',
    })
  }

  const handleUpdateEmail = (emails: Chip[]) => {
    dispatch({
      type: 'UPDATE_EMAILS',
      payload: emails,
    })
  }

  const handleUpdateEmailSubject = (subject: string) => {
    dispatch({
      type: 'UPDATE_EMAIL_SUBJECT',
      payload: subject,
    })
  }

  const handleUpdateEmailBody = (body: string) => {
    dispatch({
      type: 'UPDATE_EMAIL_BODY',
      payload: body,
    })
  }

  const handleUpdatePaymentAmount = (amount: PaymentAmount) => {
    dispatch({
      type: 'UPDATE_PARTIAL_PAYMENT_AMOUNT',
      payload: amount,
    })
  }

  const handleUpdatePaymentName = (name: string) => {
    dispatch({
      type: 'UPDATE_PARTIAL_PAYMENT_NAME',
      payload: name,
    })
  }

  const handleUpdateDueDate = (dueDate: string) => {
    dispatch({
      type: 'UPDATE_DUE_DATE',
      payload: dueDate,
    })
  }

  const handleResetPayment = () => {
    dispatch({
      type: 'RESET_PAYMENT',
    })
  }

  return {
    emailFields,
    handleUploading,
    handleDeleteAttachment,
    handleUpdateAttachmentLoading,
    handleUpdateEmail,
    handleUpdateEmailBody,
    handleUpdateEmailSubject,
    handleUpdatePaymentAmount,
    handleUpdatePaymentName,
    handleUpdateDueDate,
    handleResetPayment,
  }
}
